import posthog from 'posthog-js';
import type { Tracker } from '../../types';
import type { GdprSettings } from '@/providers/Cookie/types';

/**
 * PostHog tracker implementation.
 *
 * PostHog is initialized once by the `PostHogProvider` at the app root
 * (see src/main.tsx). This tracker forwards custom events to the shared
 * posthog-js singleton so events sent through the EventTracker /
 * TrackingService also reach PostHog. Capturing (including session replay) is
 * opt-out by default and only enabled once analytics consent is granted.
 */
export class PostHogTracker implements Tracker {
  private get isReady(): boolean {
    return posthog.__loaded === true;
  }

  init(): void {
    // No-op: PostHogProvider initializes the posthog-js singleton at the app root.
  }

  trackPageView(): void {
    // No-op: PostHog captures page views automatically (capture_pageview default).
  }

  trackEvent(eventName: string, props?: Record<string, unknown>): void {
    if (!this.isReady) {
      return;
    }

    posthog.capture(eventName, props);
  }

  updateConsent(consents: GdprSettings['consents']): void {
    if (!this.isReady) {
      return;
    }

    if (consents.analytics) {
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }
  }

  identify(userId: string, props?: Record<string, unknown>): void {
    if (!this.isReady) {
      return;
    }

    posthog.identify(userId, props);
  }

  register(props: Record<string, unknown>): void {
    if (!this.isReady) {
      return;
    }

    posthog.register(props);
  }

  reset(): void {
    if (!this.isReady) {
      return;
    }

    posthog.reset();
  }
}
